/**
 * Spend insights view (task 21.2): per-category spend totals for an account
 * over a chosen date range (R5.1).
 *
 * The customer picks an account and a start/end date (defaulting to the last
 * 30 days) and submits. The service returns per-category totals — excluding
 * reversed/reversing transactions, with an "Uncategorized" bucket, sorted by
 * total desc then name asc (R5.3–R5.5). An empty result is valid and shown as
 * such (R5.6). An invalid range surfaces as `INVALID_DATE_RANGE` (R5.8), which
 * renders inline (against the date fields when the service attributes it).
 *
 * The default 30-day range loads automatically once the accounts have arrived
 * (and again whenever the account select changes), so the page shows data
 * immediately; the "Show insights" button remains for date-range changes. The
 * auto-load is guarded by a ref holding the last auto-loaded account id so it
 * runs once per account, not on every re-render.
 */

import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';

import {
  accountsApi,
  insightsApi,
  type InsightsResult,
} from '../api/index.js';
import { ErrorNotice, fieldErrorMessage } from '../ui/ErrorNotice.js';
import { categoryVisual, TONE_HEX } from '../ui/categoryVisual.js';
import { computeDonut, type DonutInput } from '../ui/donut.js';
import { formatMoney, isoDaysAgo, todayIso } from '../ui/format.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

/**
 * SVG donut geometry constants. The ring is drawn with `stroke-dasharray` on a
 * circle of radius {@link DONUT_RADIUS}; {@link DONUT_CIRCUMFERENCE} is the arc
 * unit segments are laid out on. {@link DONUT_MIN_ARC} keeps a tiny non-zero
 * slice visible (mirrors the existing `Math.max(4, …)` bar rule) without
 * distorting the reported percentages.
 */
const DONUT_SIZE = 168;
const DONUT_RADIUS = 60;
const DONUT_STROKE = 22;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
const DONUT_MIN_ARC = DONUT_CIRCUMFERENCE * 0.015;

/** Insights panel: account picker + date range + category totals table. */
export function InsightsView(): JSX.Element {
  const accounts = useAsyncData(() => accountsApi.listAccounts(), []);
  const [accountId, setAccountId] = useState('');
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(todayIso());
  const [result, setResult] = useState<InsightsResult | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);

  const accountSelectId = useId();
  const startId = useId();
  const endId = useId();

  const load = useAction(insightsApi.getInsights);

  const accountList = accounts.data?.accounts ?? [];
  const resolvedAccountId = accountId || (accountList[0]?.accountId ?? '');
  const currency = accountList.find((a) => a.accountId === resolvedAccountId)?.currency ?? '';

  /** Run the insights load for the current account + range (shared by submit + auto-load). */
  const runInsights = load.run;
  const runLoad = useCallback(async (): Promise<void> => {
    setSubmitted(true);
    const data = await runInsights(resolvedAccountId, startDate, endDate);
    setResult(data);
  }, [runInsights, resolvedAccountId, startDate, endDate]);

  // Auto-load once per account: on first render after accounts arrive, and
  // again when the account select changes. Date-range edits still go through
  // the button.
  const lastAutoLoaded = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (accounts.loading || resolvedAccountId === '') {
      return;
    }
    if (lastAutoLoaded.current === resolvedAccountId) {
      return;
    }
    lastAutoLoaded.current = resolvedAccountId;
    void runLoad();
  }, [accounts.loading, resolvedAccountId, runLoad]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await runLoad();
  }

  const startError = fieldErrorMessage(load.error, 'startDate');
  const endError = fieldErrorMessage(load.error, 'endDate');
  const rangeError = fieldErrorMessage(load.error, 'dateRange');
  const generalError =
    startError === undefined && endError === undefined && rangeError === undefined
      ? load.error
      : undefined;

  return (
    <section aria-labelledby="insights-heading">
      <h2 id="insights-heading" className="esb-section-title">Spend insights</h2>

      <ErrorNotice error={accounts.error} />

      <form onSubmit={(e) => void onSubmit(e)} aria-labelledby="insights-heading" noValidate className="esb-card">
        <div className="esb-field">
          <label htmlFor={accountSelectId} className="esb-field__label">Account</label>
          <select
            id={accountSelectId}
            className="esb-select"
            value={resolvedAccountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {accountList.map((a) => (
              <option key={a.accountId} value={a.accountId}>
                {a.accountType}
              </option>
            ))}
          </select>
        </div>

        <div className="esb-field">
          <label htmlFor={startId} className="esb-field__label">Start date</label>
          <input
            id={startId}
            className="esb-field__input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-invalid={startError !== undefined || rangeError !== undefined}
            required
          />
          {startError !== undefined ? <span role="alert" className="esb-inline-error">{startError}</span> : null}
        </div>

        <div className="esb-field">
          <label htmlFor={endId} className="esb-field__label">End date</label>
          <input
            id={endId}
            className="esb-field__input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-invalid={endError !== undefined || rangeError !== undefined}
            required
          />
          {endError !== undefined ? <span role="alert" className="esb-inline-error">{endError}</span> : null}
        </div>

        {rangeError !== undefined ? <p role="alert" className="esb-inline-error">{rangeError}</p> : null}
        <ErrorNotice error={generalError} />

        <button type="submit" className="esb-btn esb-btn--primary" disabled={load.pending || resolvedAccountId === ''}>
          {load.pending ? 'Loading…' : 'Show insights'}
        </button>
      </form>

      {submitted && !load.pending && load.error === undefined && result !== undefined ? (
        result.categories.length === 0 ? (
          <p className="esb-empty">No spend in the selected range.</p>
        ) : (
          <SpendBreakdown
            categories={result.categories}
            currency={result.currency || currency}
            startDate={startDate}
            endDate={endDate}
          />
        )
      ) : null}
    </section>
  );
}

/**
 * The spend breakdown: a colour-matched SVG donut headline plus the accessible
 * per-category list carrying the exact numbers. The donut is a visual aid
 * (`role="img"` with a summarising label); the list is the textual data.
 */
function SpendBreakdown({
  categories,
  currency,
  startDate,
  endDate,
}: {
  readonly categories: InsightsResult['categories'];
  readonly currency: string;
  readonly startDate: string;
  readonly endDate: string;
}): JSX.Element {
  const { segments, total } = computeDonut(categories, DONUT_CIRCUMFERENCE, DONUT_MIN_ARC);
  const totalLabel = formatMoney(total.toFixed(2), currency);

  // A concise textual summary of the ring for assistive tech (top few shares).
  const ariaLabel =
    `Spend by category from ${startDate} to ${endDate}. Total ${totalLabel}. ` +
    segments
      .slice(0, 6)
      .map((s) => `${s.item.category} ${Math.round(s.percent)}%`)
      .join(', ') +
    (segments.length > 6 ? `, and ${segments.length - 6} more` : '') +
    '.';

  const center = DONUT_SIZE / 2;

  return (
    <div className="esb-card esb-insights">
      {/* Donut is a visual aid only; the list below carries the numbers. */}
      <div
        className="esb-donut"
        role="img"
        aria-label={ariaLabel}
      >
        <svg
          className="esb-donut__svg"
          viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
          width={DONUT_SIZE}
          height={DONUT_SIZE}
          aria-hidden="true"
          focusable="false"
        >
          {/* Track ring behind the segments. */}
          <circle
            cx={center}
            cy={center}
            r={DONUT_RADIUS}
            fill="none"
            stroke="var(--esb-surface-2)"
            strokeWidth={DONUT_STROKE}
          />
          {/* One arc per category, rotated so 0% starts at the top (12 o'clock). */}
          <g transform={`rotate(-90 ${center} ${center})`}>
            {segments.map((seg) => {
              const { tone } = categoryVisual(seg.item.category);
              return (
                <circle
                  key={seg.item.category}
                  className="esb-donut__seg"
                  cx={center}
                  cy={center}
                  r={DONUT_RADIUS}
                  fill="none"
                  stroke={TONE_HEX[tone]}
                  strokeWidth={DONUT_STROKE}
                  strokeDasharray={`${seg.dashLength} ${DONUT_CIRCUMFERENCE - seg.dashLength}`}
                  strokeDashoffset={seg.dashOffset}
                />
              );
            })}
          </g>
        </svg>
        <span className="esb-donut__center" aria-hidden="true">
          <span className="esb-donut__amount">{totalLabel}</span>
          <span className="esb-donut__label">Total</span>
        </span>
      </div>

      <ul
        className="esb-bars"
        aria-label={`Spend by category from ${startDate} to ${endDate}`}
      >
        {categories.map((row) => {
          const { Icon, tone } = categoryVisual(row.category);
          return (
            <li key={row.category} className="esb-bar-row">
              <span className="esb-bar-row__head">
                <span className="esb-bar-row__name">
                  <span
                    className={`esb-cat esb-cat--${tone} esb-bar-row__icon`}
                    aria-hidden="true"
                  >
                    <Icon className="esb-cat__icon" />
                  </span>
                  <span className="esb-bar-row__label">{row.category}</span>
                </span>
                <span className="esb-bar-row__total">
                  {formatMoney(row.total, currency)}
                </span>
              </span>
              <span className="esb-bar-track" aria-hidden="true">
                <span
                  className="esb-bar-fill"
                  style={{
                    width: `${barWidth(row.total, categories)}%`,
                    background: TONE_HEX[tone],
                  }}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Parse a decimal-string total to a number for proportional bar widths.
 * Presentation-only: does not affect the exact `formatMoney` display. */
function toNumber(total: string): number {
  const value = Number(total);
  return Number.isFinite(value) ? Math.abs(value) : 0;
}

/** Width (%) of a category's bar relative to the largest category total. */
function barWidth(
  total: string,
  categories: readonly DonutInput[],
): number {
  const max = categories.reduce((m, c) => Math.max(m, toNumber(c.total)), 0);
  if (max <= 0) {
    return 0;
  }
  const pct = (toNumber(total) / max) * 100;
  // Keep a small minimum so a non-zero total is always visible.
  return Math.max(4, Math.round(pct));
}
